/**
 * Apps (D354, `apps.list`): the small screens its person made or added on the workbench, with what each is for. An
 * app the workbench keeps from this reader says why. Each app runs on the workbench, where its reads and actions are
 * checked; the console lists them, and they open in AI Workbench and in the phone app.
 */

import { Cubes3 } from '@gravity-ui/icons';

import { ArtEmpty, ListGroup, ListRow, StatusMark } from '../../components/ui.tsx';
import type { ArtTone } from '../../components/ui/Art.tsx';
import { useLoad } from '../../hooks.ts';
import type { Workbench } from '../useWorkbench.ts';
import { RemoteView } from './RemoteView.tsx';

const TONES: readonly ArtTone[] = ['teal', 'violet', 'amber', 'blue', 'rose', 'green', 'slate'];

interface AppRow {
  id: string;
  name: string;
  purpose: string;
  tone: ArtTone;
  blocked?: string;
}

function readApp(raw: unknown): AppRow | undefined {
  const r = (raw ?? {}) as Record<string, unknown>;
  if (typeof r.id !== 'string' || typeof r.name !== 'string') return undefined;
  return {
    id: r.id,
    name: r.name,
    purpose: typeof r.purpose === 'string' ? r.purpose : '',
    tone: (TONES as readonly string[]).includes(String(r.tone)) ? (r.tone as ArtTone) : 'teal',
    ...(typeof r.blocked === 'string' && r.blocked ? { blocked: r.blocked } : {}),
  };
}

export function AppsView({ w }: { w: Workbench }) {
  const apps = useLoad(
    async () => {
      if (!w.connected) return undefined;
      try {
        const r = await w.tryCall<{ apps?: unknown[] }>('apps.list', {});
        return r ? { kind: 'apps' as const, apps: (r.apps ?? []).flatMap((a) => readApp(a) ?? []) } : { kind: 'too-old' as const };
      } catch (e) {
        return /-32001|scope/i.test((e as Error).message) ? { kind: 'scope' as const } : Promise.reject(e);
      }
    },
    [w.connected],
    w.connected ? 120_000 : undefined,
    { whenVisible: true },
  );
  const a = apps.data;
  return (
    <RemoteView narrow>
      {!w.connected && !a ? (
        <ArtEmpty tone="teal" icon={<Cubes3 />} title="The workbench is not connected">
          Its apps show here while it runs and is connected to your organisation.
        </ArtEmpty>
      ) : a?.kind === 'too-old' ? (
        <ArtEmpty tone="teal" icon={<Cubes3 />} title="This workbench needs an update to list its apps here" />
      ) : a?.kind === 'scope' ? (
        <ArtEmpty tone="teal" icon={<Cubes3 />} title="This workbench keeps its apps to itself">
          It was not allowed to show its apps to the organisation.
        </ArtEmpty>
      ) : a?.kind === 'apps' && !a.apps.length ? (
        <ArtEmpty tone="teal" icon={<Cubes3 />} title="No apps yet">
          Apps its person makes or adds in AI Workbench show here.
        </ArtEmpty>
      ) : a?.kind === 'apps' ? (
        <ListGroup title="Apps" count={a.apps.length} description="They open in AI Workbench and in the phone app.">
          {a.apps.map((x) => (
            <ListRow key={x.id} icon={<Cubes3 />} title={x.name} description={x.blocked ?? x.purpose} status={x.blocked ? <StatusMark state="disabled" label="Kept back" /> : undefined} />
          ))}
        </ListGroup>
      ) : (
        <p className="m-0 text-sm text-muted">Asking the workbench for its apps…</p>
      )}
    </RemoteView>
  );
}
